import type { ComicStatus } from './comic-status'
import { comicStatus } from './comic-status'
import type { LibraryEntry } from './library-entries'
import type { ShelfItem } from './shelf-items'

export type CollectionCell = Extract<ShelfItem, { kind: 'collection' }>

/**
 * 集合格要點的燈：只有成員的本機檔案出了問題才點（遺失優先於索引失敗），備份狀態不點——
 * 集合裡混著已備份與未備份是常態，一顆燈講不清楚。
 */
export function collectionStatus(
  members: readonly LibraryEntry[],
  local: { missing: ReadonlySet<string>; indexErrors: Readonly<Record<string, unknown>>; indexing: ReadonlySet<string> },
): ComicStatus | null {
  let worst: ComicStatus | null = null
  for (const member of members) {
    if (!member.local) continue
    const status = comicStatus({
      hasLocalCopy: true, syncState: member.comic.syncState,
      missing: local.missing.has(member.local.id), indexFailed: !!local.indexErrors[member.local.id], indexing: local.indexing.has(member.local.id),
    })
    if (status.kind === 'missing') return status
    if (status.kind === 'index-failed') worst = status
  }
  return worst
}

/**
 * 集合格的進度條：集合模式是「已讀完 N／M 本」，合併模式是合併冊的位置（最新成員進度的絕對頁碼／總頁數）。
 * 合併模式沒有任何進度時回 null，卡片不顯示進度條——與單本作品相同。
 */
export function collectionProgress(cell: CollectionCell): { percent: number; label: string } | null {
  if (cell.mode === 'merged') {
    if (!cell.mergedProgress) return null
    const { page, pageCount } = cell.mergedProgress
    const percent = pageCount > 1 ? Math.round((page / (pageCount - 1)) * 100) : 0
    return { percent, label: `${cell.collection.title}，讀到第 ${page + 1}／${pageCount} 頁` }
  }
  const total = cell.collection.memberCount
  const percent = total ? Math.round((cell.readCount / total) * 100) : 0
  return { percent, label: `${cell.collection.title}，已讀完 ${cell.readCount}／${total} 本` }
}

/** 集合格右下角的記號：集合模式「N 本」，合併模式「合併 · N 話」。 */
export function collectionBadge(cell: CollectionCell): string {
  return cell.mode === 'merged' ? `合併 · ${cell.collection.memberCount} 話` : `${cell.collection.memberCount} 本`
}
